import AnalysisLensHelperText from '@components/common/AnalysisLensHelperText';
import { IncludedTranscripts } from '@components/interview/analysis-components/IncludeTranscripts';
import { Metadata } from '@components/interview/analysis-components/MetaData';
import { PriorityInsights } from '@components/interview/analysis-components/ProrityInsight';
import { Summary } from '@components/interview/analysis-components/Summary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HubIcon from '@mui/icons-material/Hub'; // Example Icon for Outcome-Barrier
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import { AnalysisLayout } from './layout';

// Helper to safely parse JSON results
const safeParseResults = (results) => {
  if (!results) return null;
  try {
    // If results is already an object (likely), return it directly.
    // If it's a string, attempt to parse it.
    return typeof results === 'string' ? JSON.parse(results) : results;
  } catch (error) {
    console.error('Error parsing OutcomeBarrierAnalysis results:', error);
    return null; // Indicate parsing failure
  }
};

// Styled component for progress bar labels
const ProgressBarBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(1),
}));

const ProgressBarLabel = styled(Typography)(({ theme }) => ({
  minWidth: 120, // Ensure labels align
  marginRight: theme.spacing(1),
}));

const ProgressBarValue = styled(Typography)(({ theme }) => ({
  minWidth: 30, // Space for percentage
  textAlign: 'right',
  marginLeft: theme.spacing(1),
}));

const OutcomeBarrierAnalysis = ({ item }) => {
  if (!item) {
    return (
      <Typography variant="body2" color="text.secondary">
        Analysis item data is missing.
      </Typography>
    );
  }

  const { analysis_lens_type, results: rawResults } = item;
  const results = safeParseResults(rawResults);

  if (!results) {
    return (
      <Paper elevation={2} sx={{ p: 3, mt: 2, bgcolor: 'error.light' }}>
        <Typography variant="h6" gutterBottom color="error.contrastText">
          Outcome-Barrier Matrix Analysis Error
        </Typography>
        <Typography color="error.contrastText">
          Could not load or parse the analysis results. Please check the data format.
        </Typography>
      </Paper>
    );
  }

  // Destructure safely with defaults
  const {
    transcripts_analyzed = 0,
    total_unique_outcomes = 0,
    total_unique_barriers = 0,
    barrier_type_distribution = {
      systemic: 0,
      informational: 0,
      emotional: 0,
      behavioral: 0,
      logistical: 0,
    },
    barrier_prevalence = [],
    outcome_prevalence = [],
    high_leverage_opportunities = [],
  } = results;

  // Calculate total barriers for percentage calculation
  const totalBarriersCount = Object.values(barrier_type_distribution).reduce(
    (sum, count) => sum + count,
    0,
  );

  const renderBarrierDistribution = () => {
    if (totalBarriersCount === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          No barrier distribution data available.
        </Typography>
      );
    }

    return Object.entries(barrier_type_distribution).map(([type, count]) => {
      const percentage =
        totalBarriersCount > 0 ? Math.round((count / totalBarriersCount) * 100) : 0;
      return (
        <ProgressBarBox key={type}>
          <ProgressBarLabel variant="body2" sx={{ textTransform: 'capitalize' }}>
            {type}:
          </ProgressBarLabel>
          <Box sx={{ width: '100%', mr: 1 }}>
            <LinearProgress variant="determinate" value={percentage} />
          </Box>
          <ProgressBarValue variant="body2">
            {`${percentage}%`} ({count})
          </ProgressBarValue>
        </ProgressBarBox>
      );
    });
  };

  const renderQuotes = (quotes) => (
    <List dense disablePadding sx={{ pl: 2, mt: 1, borderLeft: '2px solid #eee' }}>
      {quotes.map((quote, index) => (
        <ListItem key={index} disableGutters sx={{ pt: 0, pb: 0.5 }}>
          <ListItemText
            primaryTypographyProps={{
              variant: 'caption',
              fontStyle: 'italic',
              color: 'text.secondary',
            }}
          >
            {quote}
          </ListItemText>
        </ListItem>
      ))}
    </List>
  );

  const renderCommonBarriers = (barriers) => (
    <Box sx={{ mt: 1 }}>
      <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
        Common Barriers:
      </Typography>
      <List dense disablePadding sx={{ pl: 2 }}>
        {barriers.map((barrier, index) => (
          <ListItem key={index} disableGutters sx={{ pt: 0, pb: 0 }}>
            <ListItemText primaryTypographyProps={{ variant: 'caption' }}>- {barrier}</ListItemText>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <AnalysisLayout results={results} item={{ ...item, name: 'Outcome-Barrier Matrix Analysis' }}>
      <CardHeader
        title={
          <Typography
            variant="h5"
            gutterBottom
            component="span"
            sx={{ display: 'inline-flex', alignItems: 'center' }}
          >
            <HubIcon sx={{ verticalAlign: 'bottom', mr: 1 }} />
            Outcome-Barrier Matrix Analysis
            {analysis_lens_type && (
              <AnalysisLensHelperText lensName={analysis_lens_type} level="run" size="small" />
            )}
          </Typography>
        }
      />
      <Divider />
      <CardContent>
        <Metadata item={item} />
        <Summary results={results} item={item} />

        {/* Counts Section */}
        <Grid container spacing={2} sx={{ mb: 3, textAlign: 'center' }}>
          <Grid item xs={4}>
            <Typography variant="h4">{transcripts_analyzed}</Typography>
            <Typography variant="caption">Transcripts Analyzed</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="h4">{total_unique_outcomes}</Typography>
            <Typography variant="caption">Unique Outcomes</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="h4">{total_unique_barriers}</Typography>
            <Typography variant="caption">Unique Barriers</Typography>
          </Grid>
        </Grid>

        {/* Barrier Distribution Section */}
        <Card variant="outlined" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Barrier Type Distribution
          </Typography>
          {renderBarrierDistribution()}
        </Card>

        {/* Prevalent Barriers Section */}
        <Card variant="outlined" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Prevalent Barriers ({barrier_prevalence.length})
          </Typography>
          {barrier_prevalence.length > 0 ? (
            barrier_prevalence.map((b, index) => (
              <Accordion key={index} TransitionProps={{ unmountOnExit: true }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      width: '100%',
                      alignItems: 'center',
                      pr: 1,
                    }}
                  >
                    <Typography sx={{ flexGrow: 1, mr: 1 }}>{b.barrier}</Typography>
                    <Chip label={`${b.barrier_type} (${b.prevalence})`} size="small" />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>{renderQuotes(b.representative_quotes)}</AccordionDetails>
              </Accordion>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              No prevalent barrier data found.
            </Typography>
          )}
        </Card>

        {/* Prevalent Outcomes Section */}
        <Card variant="outlined" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Prevalent Outcomes ({outcome_prevalence.length})
          </Typography>
          {outcome_prevalence.length > 0 ? (
            outcome_prevalence.map((o, index) => (
              <Accordion key={index} TransitionProps={{ unmountOnExit: true }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      width: '100%',
                      alignItems: 'center',
                      pr: 1,
                    }}
                  >
                    <Typography sx={{ flexGrow: 1, mr: 1 }}>{o.outcome}</Typography>
                    <Chip label={`Prevalence: ${o.prevalence}`} size="small" />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {o.common_barriers?.length > 0 && renderCommonBarriers(o.common_barriers)}
                  {o.representative_quotes?.length > 0 && renderQuotes(o.representative_quotes)}
                </AccordionDetails>
              </Accordion>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              No prevalent outcome data found.
            </Typography>
          )}
        </Card>

        {/* High Leverage Opportunities */}
        <Card variant="outlined" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            High Leverage Opportunities
          </Typography>
          {high_leverage_opportunities.length > 0 ? (
            <List dense>
              {high_leverage_opportunities.map((opp, index) => (
                <ListItem key={index}>
                  <ListItemText primary={opp} />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No high leverage opportunities identified.
            </Typography>
          )}
        </Card>

        <PriorityInsights results={results} />
        <IncludedTranscripts item={item} results={results} />
      </CardContent>
    </AnalysisLayout>
  );
};

OutcomeBarrierAnalysis.propTypes = {
  /** The analysis item object containing results and metadata */
  item: PropTypes.shape({
    _id: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    study_id: PropTypes.string,
    created_at: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    analysis_lens_type: PropTypes.string,
    results: PropTypes.oneOfType([PropTypes.string, PropTypes.object]), // Can be stringified JSON or parsed object
  }),
};

export default OutcomeBarrierAnalysis;
